import { projectedDocumentHistory } from './workflowDocumentHistory';
import { useMemo } from 'react';
import { normalizeHomeChatCards } from './types';
import { getWorkflowOnlyPresentation } from './workflowOnlyPresentation';
import PurchaseFollowupInput from './PurchaseFollowupInput';
import HomeChatBoundWorkflowView from './HomeChatBoundWorkflowView';
import WorkflowControllerTranscript, { projectWorkflowControllerTranscript } from './WorkflowControllerTranscript';
import { useDocumentAssistant } from './DocumentAssistantContext';
import { DocumentAssistantComposer, DocumentAssistantTurn, isCompletedDocumentEdit, WORKFLOW_QUERY_PLACEHOLDER } from './DocumentAssistantConversation';
import { alpha } from '@mui/material/styles';
import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Drawer from '@mui/material/Drawer';
import SessionsRail from './SessionsRail';
import HomeChatWorkflowBanner from './HomeChatWorkflowBanner';
import ChatThread from '../FarChat/ChatThread';
import MessageInput from '../FarChat/MessageInput';
import AttachmentChipRow from '../FarChat/AttachmentChipRow';
import DropOverlay from '../FarChat/DropOverlay';
import useDragDropUpload from '../FarChat/useDragDropUpload';
import FarChatResponse from '../FarChat/FarChatResponse';
import ClarificationCard from '../FarChat/ClarificationCard';
import TopicFocusCard from '../FarChat/TopicFocusCard';
import StepProgressChecklist from '../FarChat/StepProgressChecklist';
import SimilarRequirementsCards from './SimilarRequirementsCards';
import { buildLeadStep } from '../FarChat/useTurnProgress';
import { HomeChatPanelProps as Props } from './HomeChatPanel.types';
import { actionChoicePendingLabel } from './homeChatChoiceUtils';
import type { HomeChatPanelState } from './useHomeChatPanelState';
import type { HomeChatSessions } from './useHomeChatSessions';
import type { HomeChatSessionActions } from './useHomeChatSessionActions';
import type { HomeChatWorkflowHandoffs } from './useHomeChatWorkflowHandoffs';
import type { HomeChatTurnEditing } from './useHomeChatTurnEditing';
import type { HomeChatCardActions } from './useHomeChatCardActions';
import type { HomeChatComposer } from './useHomeChatComposer';



export type HomeChatPanelViewProps = Pick<HomeChatCardActions, 'currentRaFollowupCard'> &
Pick<HomeChatPanelState, 'messages' | 'mrNotificationBoundResumeId' | 'mrObserverNotificationId' | 'workflowOnlyEntryMessage'> &
Pick<HomeChatWorkflowHandoffs, 'notifyBoundMrQuestions' | 'rehydrateBoundMrChat' | 'shareAndNotifyBoundMrQuestions' | 'submitNotificationBoundMrTurn' | 'submitSectionCHandoff' | 'submitWorkflowOnlyChoice'> &
Pick<Props, 'onBackConstrainedWorkflowEntry' | 'onRetryConstrainedWorkflowEntry'> &
Pick<Props, 'allowedWorkflowTypes' | 'constrainedWorkflowEntry' | 'initialSessionId' | 'workflowOnly' | 'workflowOnlyAudience'> &
Pick<HomeChatPanelState, 'constrainedOptimisticSessionId' | 'failedPurchaseFollowup' | 'newPurchaseGuidanceFocusId' | 'setNewPurchaseGuidanceFocusId' | 'workflowOnlyChoice' | 'workflowOnlyGuidance' | 'workflowOnlyPurchaseGuidance'> &
Pick<HomeChatCardActions, 'currentPurchaseClarification' | 'handlePurchaseClarification' | 'handleSelectPurchaseGuidance' | 'handleSelectMissionPartnerGuidance'> &
Pick<HomeChatPanelState,
  | 'rehydratingGpcSessionId'
  | 'activeSessionId'
  | 'workflowDocumentHistory'
  | 'sessions'
  | 'setMobileSessionsOpen'
  | 'sessionsLoading'
  | 'isCompanion'
  | 'mobileSessionsOpen'
  | 'mrChatView'
  | 'constructionSowChatView'
  | 'servicesPwsChatView'
  | 'gpcChatView'
  | 'optimisticGpcOpening'
  | 'pendingIntentChoiceSelection'
  | 'activeSessionIdRef'
  | 'mrTurnInFlight'
  | 'inFlight'
  | 'pendingRequestId'
  | 'notice'
  | 'setNotice'
  | 'threadScrollRef'
  | 'conversationItems'
  | 'busySimilarRequirementsCardId'
  | 'busyWorkflowRecommendationCardId'
  | 'busyActionChoiceLabel'
  | 'chatCards'
  | 'busyTrainingActionId'
  | 'editingTurnId'
  | 'editDraft'
  | 'setEditDraft'
  | 'replacingTurnId'
  | 'turns'
  | 'pendingGpcRouter'
  | 'pendingClarification'
  | 'pendingTopicClarification'
  | 'inFlightUserMsg'
  | 'inFlightClarification'
  | 'inFlightTopicFocus'
  | 'attachments'
  | 'inputContainerRef'
  | 'composerPlaceholder'
> &
Pick<HomeChatSessions,
  | 'handleSelectSession'
  | 'handleNewChat'
  | 'gpcHandoffProgressLabel'
  | 'progress'
> &
Pick<HomeChatSessionActions,
  | 'handleRename'
  | 'handleDelete'
  | 'handleRemoveAttachment'
  | 'handleAttach'
> &
Pick<Props, 'onOpenTraining' | 'onOpenChatExamples'> &
Pick<Props, 'displayMode'> &
Pick<HomeChatWorkflowHandoffs,
  | 'submitBoundMrTurn'
  | 'uploadMrFiles'
  | 'openMrWorkflow'
  | 'submitBoundServicesPwsTurn'
  | 'uploadServicesRequirement'
  | 'extractServicesRequirement'
  | 'uploadServicesSupport'
  | 'removeServicesRequirement'
  | 'replaceServicesSupport'
  | 'removeServicesSupport'
  | 'openGeneratedServicesPws'
  | 'submitBoundConstructionSowTurn'
  | 'uploadConstructionRequirement'
  | 'extractConstructionRequirement'
  | 'uploadConstructionSupport'
  | 'removeConstructionRequirement'
  | 'replaceConstructionSupport'
  | 'removeConstructionSupport'
  | 'openGeneratedConstructionSow'
  | 'submitBoundGpcTurn'
  | 'openGpcWorkflow'
  | 'submitGpcConfirmation'
> &
Pick<HomeChatTurnEditing,
  | 'processingStopControl'
  | 'editableLatestTurnId'
  | 'handleBeginEdit'
  | 'handleSaveAndResendEdit'
  | 'handleCancelEdit'
  | 'activeSession'
> &
{
  documentCompanion?: boolean;
  controllerAvailable?: boolean;
  controllerOpen?: boolean;
  onOpenController?: () => void;
  onReturnDocument?: () => void;
  onDocumentSend?: (message: string, intent?: 'edit' | 'question') => Promise<void>;
  completionSoundControl: React.ReactElement | null;
  dragHandlers: ReturnType<typeof useDragDropUpload>['dragHandlers'];
  showEmptyState: boolean;
  isDragging: boolean;
} &
Pick<HomeChatCardActions,
  | 'handleSelectSimilarRequirementsType'
  | 'handleCancelSimilarRequirements'
  | 'handleOpenSimilarRequirementsResult'
  | 'handleSelectAmbiguousIntentChoice'
  | 'handleSelectWorkflowRecommendation'
  | 'handleOpenCreatedWorkflow'
  | 'handleSelectActionChoice'
  | 'handleSelectTrainingAction'
> &
Pick<HomeChatComposer,
  | 'handleResolveClarification'
  | 'handleResolveTopicFocus'
  | 'handleSend'
>;

export const HomeChatPanelView: React.FC<HomeChatPanelViewProps> = ({
  documentCompanion = false,
  controllerAvailable = false,
  controllerOpen = false,
  onOpenController,
  onReturnDocument,
  onDocumentSend,
  allowedWorkflowTypes,
  constrainedWorkflowEntry,
  initialSessionId,
  workflowOnly,
  workflowOnlyAudience,
  constrainedOptimisticSessionId,
  failedPurchaseFollowup,
  newPurchaseGuidanceFocusId,
  setNewPurchaseGuidanceFocusId,
  workflowOnlyChoice,
  workflowOnlyGuidance,
  workflowOnlyPurchaseGuidance,
  currentPurchaseClarification,
  handlePurchaseClarification,
  handleSelectMissionPartnerGuidance,
  handleSelectPurchaseGuidance,
  currentRaFollowupCard,
  messages,
  mrNotificationBoundResumeId,
  mrObserverNotificationId,
  workflowOnlyEntryMessage,
  notifyBoundMrQuestions,
  rehydrateBoundMrChat,
  shareAndNotifyBoundMrQuestions,
  submitNotificationBoundMrTurn,
  submitSectionCHandoff,
  submitWorkflowOnlyChoice,
  onBackConstrainedWorkflowEntry,
  onRetryConstrainedWorkflowEntry,
  onOpenChatExamples,
  rehydratingGpcSessionId,
  activeSessionId,
  workflowDocumentHistory,
  sessions,
  setMobileSessionsOpen,
  handleSelectSession,
  handleNewChat,
  handleRename,
  handleDelete,
  sessionsLoading,
  isCompanion,
  mobileSessionsOpen,
  mrChatView,
  constructionSowChatView,
  servicesPwsChatView,
  gpcChatView,
  optimisticGpcOpening,
  pendingIntentChoiceSelection,
  displayMode,
  submitBoundMrTurn,
  activeSessionIdRef,
  uploadMrFiles,
  openMrWorkflow,
  mrTurnInFlight,
  inFlight,
  pendingRequestId,
  processingStopControl,
  completionSoundControl,
  submitBoundServicesPwsTurn,
  uploadServicesRequirement,
  extractServicesRequirement,
  uploadServicesSupport,
  removeServicesRequirement,
  replaceServicesSupport,
  removeServicesSupport,
  openGeneratedServicesPws,
  submitBoundConstructionSowTurn,
  uploadConstructionRequirement,
  extractConstructionRequirement,
  uploadConstructionSupport,
  removeConstructionRequirement,
  replaceConstructionSupport,
  removeConstructionSupport,
  openGeneratedConstructionSow,
  submitBoundGpcTurn,
  openGpcWorkflow,
  gpcHandoffProgressLabel,
  notice,
  setNotice,
  dragHandlers,
  threadScrollRef,
  showEmptyState,
  conversationItems,
  busySimilarRequirementsCardId,
  busyWorkflowRecommendationCardId,
  handleSelectSimilarRequirementsType,
  handleCancelSimilarRequirements,
  handleOpenSimilarRequirementsResult,
  handleSelectAmbiguousIntentChoice,
  handleSelectWorkflowRecommendation,
  handleOpenCreatedWorkflow,
  handleSelectActionChoice,
  busyActionChoiceLabel,
  chatCards,
  editableLatestTurnId,
  busyTrainingActionId,
  onOpenTraining,
  handleSelectTrainingAction,
  handleBeginEdit,
  editingTurnId,
  editDraft,
  setEditDraft,
  handleSaveAndResendEdit,
  handleCancelEdit,
  replacingTurnId,
  progress,
  turns,
  activeSession,
  pendingGpcRouter,
  submitGpcConfirmation,
  pendingClarification,
  pendingTopicClarification,
  inFlightUserMsg,
  inFlightClarification,
  inFlightTopicFocus,
  handleResolveClarification,
  handleResolveTopicFocus,
  attachments,
  handleRemoveAttachment,
  inputContainerRef,
  handleSend,
  handleAttach,
  composerPlaceholder,
  isDragging,
}) => {

  const documentAssistant = useDocumentAssistant();
  const sourceScope = JSON.stringify([documentAssistant.scopeUser, documentCompanion ? documentAssistant.flowId : null, activeSessionId]);
  const sourceController = React.useRef<{ scope: string; view: NonNullable<typeof servicesPwsChatView | typeof constructionSowChatView>; services: boolean } | null>(null);
  if (sourceController.current?.scope !== sourceScope) sourceController.current = null;
  if (activeSessionId) {
    const services = !!servicesPwsChatView && servicesPwsChatView.home_chat_session_id === activeSessionId && (documentCompanion ? servicesPwsChatView.workflow?.flow_id === documentAssistant.flowId : servicesPwsChatView.cursor === 'handoff_ready');
    const construction = !!constructionSowChatView && constructionSowChatView.home_chat_session_id === activeSessionId && (documentCompanion ? constructionSowChatView.workflow?.flow_id === documentAssistant.flowId : constructionSowChatView.cursor === 'handoff_ready');
    if (services && servicesPwsChatView) sourceController.current = { scope: sourceScope, view: servicesPwsChatView, services: true };
    else if (construction && constructionSowChatView) sourceController.current = { scope: sourceScope, view: constructionSowChatView, services: false };
  }
  const mainSourceConversation = !documentCompanion && !!sourceController.current;
  const [mainControllerOpen, setMainControllerOpen] = React.useState(false);
  React.useEffect(() => { setMainControllerOpen(false); }, [sourceScope]);
  // Newly requested controllers remain reachable while retaining the source
  // conversation in its own scope. Reading history never resumes a controller.
  const newController = gpcChatView?.session_id || mrChatView?.session_id || null;
  React.useEffect(() => { if (newController) setMainControllerOpen(true); }, [newController]);
  const canonicalTurns = workflowDocumentHistory?.sessionId === activeSessionId
    ? projectedDocumentHistory(workflowDocumentHistory.items) : [];
  const documentTurns = documentCompanion ? documentAssistant.turns : canonicalTurns;
  const controllerTranscriptScope = JSON.stringify([sourceScope, sourceController.current?.view.session_id]);
  const existingUserMessages = conversationItems.flatMap(item => item.kind === 'turn' ? [item.turn.user_msg] : item.kind === 'legacy_message' && item.message.role === 'user' ? [item.message.content] : []);
  const existingAssistantMessages = conversationItems.flatMap(item => item.kind === 'turn' ? [item.turn.summary, item.turn.detail, [item.turn.summary, item.turn.detail].filter(Boolean).join('\n')] : item.kind === 'legacy_message' && item.message.role === 'assistant' ? [item.message.content] : []);
  const controllerTurns = sourceController.current
    ? projectWorkflowControllerTranscript(sourceController.current.view, sourceController.current.services, existingUserMessages, existingAssistantMessages) : [];
  const projectedItems = [
    ...conversationItems.map(item => ({ source: 'home' as const, item, timestamp: item.kind === 'turn' ? item.turn.timestamp : item.kind === 'legacy_message' ? item.message.timestamp : (item.card as { created_at?: string }).created_at })),
    ...documentTurns.map(turn => ({ source: 'document' as const, turn, timestamp: turn.completedAt })),
  ].map((entry, index) => ({ ...entry, index })).sort((left, right) => {
    const a = left.timestamp ? Date.parse(left.timestamp) : NaN;
    const b = right.timestamp ? Date.parse(right.timestamp) : NaN;
    if (Number.isFinite(a) && Number.isFinite(b)) return a - b || left.index - right.index;
    if (!Number.isFinite(a) && !Number.isFinite(b)) return left.index - right.index;
    return !Number.isFinite(a) ? -1 : 1;
  });
  const setupAnchor = projectedItems.find(item => item.source === 'document' || (item.source === 'home' && item.item.kind === 'turn'));
  const setupTranscript = controllerTurns.length > 0
    ? <WorkflowControllerTranscript key={`workflow-setup:${controllerTranscriptScope}`} scopeKey={controllerTranscriptScope} minimal={isCompanion} turns={controllerTurns} /> : null;
  const latestCompletedEdit = [...projectedItems].reverse().find(item => item.source === 'document' && isCompletedDocumentEdit(item.turn));
  const gpcHydrationPending = rehydratingGpcSessionId !== null && rehydratingGpcSessionId === activeSessionId;

  // The server classifies constrained rows after validating the durable
  // record, owner, and *current* capability projection. Never infer this
  // from legacy bindings, a title, or generic cards: doing so could expose
  // ordinary chat history after a role change. A malformed/missing
  // constrained_session is therefore omitted fail-closed.
  const workflowOnlySessions = useMemo(() => sessions.filter((session) => {
    const kind = session.constrained_session?.kind;
    if (!kind) {
      // This one zero-turn row is an in-memory bridge for a session id that
      // the server just issued to the typed CE/RA route. It is not a saved
      // chat: it stays visible only while it is the active initial entry and
      // is replaced by the server's typed projection on reconciliation.
      return session.session_id === constrainedOptimisticSessionId
        && session.session_id === activeSessionId
        && session.session_id === initialSessionId;
    }
    if (kind === 'mr') return allowedWorkflowTypes?.includes('market_research') === true;
    if (kind === 'gpc') return allowedWorkflowTypes?.includes('gpc') === true;
    if (kind === 'construction') return allowedWorkflowTypes?.includes('one_time_construction') === true;
    if (kind === 'purchase_guidance' || kind === 'mission_partner_guidance') return workflowOnlyAudience === 'ra'
      || (workflowOnlyAudience !== 'ce' && Boolean(allowedWorkflowTypes?.some((type) => type === 'market_research' || type === 'gpc')));
    // An opaque current choice is useful only while at least one constrained
    // workflow remains authorized. The server has already shape-validated it.
    return kind === 'workflow_choice' && Boolean(allowedWorkflowTypes?.some((type) => (
      type === 'market_research'
      || type === 'gpc'
      || type === 'one_time_construction'
      || type === 'sow_verification'
    )));
  }), [activeSessionId, allowedWorkflowTypes, constrainedOptimisticSessionId, initialSessionId, sessions, workflowOnlyAudience]);

  const renderSessionsRail = (mobile: boolean) => (
    <SessionsRail
      sessions={workflowOnly ? workflowOnlySessions : sessions}
      activeSessionId={activeSessionId}
      onSelect={(sessionId) => {
        if (mobile) setMobileSessionsOpen(false);
        handleSelectSession(sessionId);
      }}
      onNewChat={() => {
        if (mobile) setMobileSessionsOpen(false);
        handleNewChat();
      }}
      onOpenChatExamples={!workflowOnly && onOpenChatExamples ? () => {
        if (mobile) setMobileSessionsOpen(false);
        onOpenChatExamples();
      } : undefined}
      onRename={handleRename}
      onDelete={handleDelete}
      loading={sessionsLoading}
      constrained={workflowOnly}
    />
  );

  const desktopSessionsRail = !isCompanion ? (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' },
        height: '100%',
        flexShrink: 0,
      }}
    >
      {renderSessionsRail(false)}
    </Box>
  ) : null;

  const mobileSessionsNavigation = !isCompanion ? (
    <>
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          justifyContent: 'flex-end',
          pb: 1,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={() => setMobileSessionsOpen(true)}
          aria-label={workflowOnly ? 'Previous chats' : 'Open chat history'}
        >
          {workflowOnly ? 'Previous chats' : 'Chat history'}
        </Button>
      </Box>
      <Drawer
        anchor="left"
        open={mobileSessionsOpen}
        onClose={() => setMobileSessionsOpen(false)}
        sx={{ zIndex: (theme) => Math.max(theme.zIndex.modal, 2200) + 50 }}
        PaperProps={{
          sx: {
            width: 'min(88vw, 320px)',
            p: 1.5,
          },
        }}
      >
        <Box data-testid="mobile-sessions-drawer" sx={{ height: '100%' }}>
          {renderSessionsRail(true)}
        </Box>
      </Drawer>
    </>
  ) : null;
  const hasWorkflowOnlyGpcConfirmation = workflowOnly && pendingGpcRouter?.kind === 'start_confirmation';
  const workflowOnlyPresentation = getWorkflowOnlyPresentation(allowedWorkflowTypes, workflowOnlyAudience);
  const raPurchaseGuidance = workflowOnlyPurchaseGuidance?.kind === 'mission_partner_guidance' || workflowOnlyAudience === 'ra' || (workflowOnlyAudience !== 'ce'
    && workflowOnlyPresentation?.abilities.some((ability) => ability === 'market_research' || ability === 'gpc'))
    ? workflowOnlyPurchaseGuidance : null;
  const projectedRaChoice = workflowOnlyAudience === 'ra'
    ? normalizeHomeChatCards([workflowOnlyChoice])[0] : null;
  const raWorkflowActionChoice = projectedRaChoice?.kind === 'action_choice' ? projectedRaChoice : null;

  // Only an explicit RA audience can combine procurement and construction.
  // Other malformed projections must not render controllers or saved rows.
  if (workflowOnly && !workflowOnlyPresentation) return null;


  if ((documentCompanion ? controllerOpen : !mainSourceConversation || mainControllerOpen) && (mrChatView
    || constructionSowChatView
    || servicesPwsChatView
    || gpcChatView
    || optimisticGpcOpening
    || gpcHydrationPending
    || pendingIntentChoiceSelection?.path === 'gpc'
    || pendingIntentChoiceSelection?.path === 'mr'
    || hasWorkflowOnlyGpcConfirmation
    || workflowOnlyChoice
    || raPurchaseGuidance
    || raWorkflowActionChoice
    || workflowOnlyGuidance
    || constrainedWorkflowEntry)) {
    return <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {!isCompanion && mainSourceConversation && <Button onClick={() => setMainControllerOpen(false)}>Return to conversation</Button>}
      <Box sx={{ flex: 1, minHeight: 0 }}><HomeChatBoundWorkflowView
      sidebarAction={documentCompanion ? <Button onClick={onReturnDocument}>Return to current document chat</Button> : isCompanion && mainSourceConversation ? <Button onClick={() => setMainControllerOpen(false)}>Return to conversation</Button> : undefined}
      threadScrollRef={threadScrollRef}
      allowedWorkflowTypes={allowedWorkflowTypes}
      workflowOnlyAudience={workflowOnlyAudience}
      workflowOnlyPurchaseGuidance={workflowOnlyPurchaseGuidance}
      busyWorkflowRecommendationCardId={busyWorkflowRecommendationCardId}
      chatCards={chatCards}
      constrainedWorkflowEntry={constrainedWorkflowEntry}
      conversationItems={conversationItems}
      currentRaFollowupCard={currentRaFollowupCard}
      failedPurchaseFollowup={failedPurchaseFollowup}
      handleCancelSimilarRequirements={handleCancelSimilarRequirements}
      handleOpenSimilarRequirementsResult={handleOpenSimilarRequirementsResult}
      handlePurchaseClarification={handlePurchaseClarification}
      handleSelectAmbiguousIntentChoice={handleSelectAmbiguousIntentChoice}
      handleSelectMissionPartnerGuidance={handleSelectMissionPartnerGuidance}
      handleSelectPurchaseGuidance={handleSelectPurchaseGuidance}
      handleSelectSimilarRequirementsType={handleSelectSimilarRequirementsType}
      handleSelectWorkflowRecommendation={handleSelectWorkflowRecommendation}
      inFlightUserMsg={inFlightUserMsg}
      messages={messages}
      mrNotificationBoundResumeId={mrNotificationBoundResumeId}
      mrObserverNotificationId={mrObserverNotificationId}
      newPurchaseGuidanceFocusId={newPurchaseGuidanceFocusId}
      notifyBoundMrQuestions={notifyBoundMrQuestions}
      onBackConstrainedWorkflowEntry={onBackConstrainedWorkflowEntry}
      onRetryConstrainedWorkflowEntry={onRetryConstrainedWorkflowEntry}
      pendingGpcRouter={pendingGpcRouter}
      rehydrateBoundMrChat={rehydrateBoundMrChat}
      setNewPurchaseGuidanceFocusId={setNewPurchaseGuidanceFocusId}
      shareAndNotifyBoundMrQuestions={shareAndNotifyBoundMrQuestions}
      submitGpcConfirmation={submitGpcConfirmation}
      submitNotificationBoundMrTurn={submitNotificationBoundMrTurn}
      submitSectionCHandoff={submitSectionCHandoff}
      submitWorkflowOnlyChoice={submitWorkflowOnlyChoice}
      workflowOnly={workflowOnly}
      workflowOnlyChoice={workflowOnlyChoice}
      workflowOnlyEntryMessage={workflowOnlyEntryMessage}
      workflowOnlyGuidance={workflowOnlyGuidance}
      mrChatView={mrChatView}
      constructionSowChatView={constructionSowChatView}
      servicesPwsChatView={servicesPwsChatView}
      gpcChatView={gpcChatView}
      optimisticGpcOpening={optimisticGpcOpening}
      gpcHydrationPending={gpcHydrationPending}
      pendingIntentChoiceSelection={pendingIntentChoiceSelection}
      displayMode={displayMode}
      isCompanion={isCompanion}
      mobileSessionsNavigation={mobileSessionsNavigation}
      desktopSessionsRail={desktopSessionsRail}
      submitBoundMrTurn={submitBoundMrTurn}
      activeSessionIdRef={activeSessionIdRef}
      activeSessionId={activeSessionId}
      uploadMrFiles={uploadMrFiles}
      openMrWorkflow={openMrWorkflow}
      mrTurnInFlight={mrTurnInFlight}
      inFlight={inFlight}
      pendingRequestId={pendingRequestId}
      processingStopControl={processingStopControl}
      completionSoundControl={completionSoundControl}
      submitBoundServicesPwsTurn={submitBoundServicesPwsTurn}
      uploadServicesRequirement={uploadServicesRequirement}
      extractServicesRequirement={extractServicesRequirement}
      uploadServicesSupport={uploadServicesSupport}
      removeServicesRequirement={removeServicesRequirement}
      replaceServicesSupport={replaceServicesSupport}
      removeServicesSupport={removeServicesSupport}
      openGeneratedServicesPws={openGeneratedServicesPws}
      submitBoundConstructionSowTurn={submitBoundConstructionSowTurn}
      uploadConstructionRequirement={uploadConstructionRequirement}
      extractConstructionRequirement={extractConstructionRequirement}
      uploadConstructionSupport={uploadConstructionSupport}
      removeConstructionRequirement={removeConstructionRequirement}
      replaceConstructionSupport={replaceConstructionSupport}
      removeConstructionSupport={removeConstructionSupport}
      openGeneratedConstructionSow={openGeneratedConstructionSow}
      submitBoundGpcTurn={submitBoundGpcTurn}
      openGpcWorkflow={openGpcWorkflow}
      rehydratingGpcSessionId={rehydratingGpcSessionId}
      gpcHandoffProgressLabel={gpcHandoffProgressLabel}
      notice={notice}
      setNotice={setNotice}
    /></Box></Box>;
  }

  if (workflowOnly) {
    // The launcher normally prevents this state. Keep the panel fail-closed
    // for direct embedding or a stale projection with no constrained ability.
    if (!workflowOnlyPresentation) return null;
    return (
      <Paper variant="outlined" data-testid="home-chat-panel" data-display-mode={displayMode} sx={{ p: 2, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {mobileSessionsNavigation}
        <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
          {desktopSessionsRail}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <HomeChatWorkflowBanner
              title={workflowOnlyPresentation.panelTitle}
              subtitle={workflowOnlyPresentation.panelSubtitle}
              headingComponent="h3"
            />
            {sessionsLoading ? (
              <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 3 }}>
                <CircularProgress size={20} />
                <Typography>{workflowOnlyPresentation.emptyStatus}</Typography>
              </Box>
            ) : workflowOnlySessions.length === 0 ? (
              <Typography sx={{ pt: 3 }} color="text.secondary">
                {workflowOnlyPresentation.emptySavedCopy}
              </Typography>
            ) : (
              <Typography sx={{ pt: 3 }} color="text.secondary">
                Select a saved workflow to continue.
              </Typography>
            )}
          </Box>
        </Box>
        <Snackbar
          open={notice !== null}
          autoHideDuration={4000}
          onClose={() => setNotice(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {notice ? (
            <Alert severity={notice.severity} onClose={() => setNotice(null)} variant="filled">
              {notice.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </Paper>
    );
  }


  return (
    <Paper
      variant="outlined"
      sx={{
        p: isCompanion ? 0 : 2,
        borderRadius: isCompanion ? 0 : 2,
        border: isCompanion ? 0 : undefined,
        // The panel always fills its parent (the launcher's DialogContent,
        // which is itself bounded by the viewport). Height/flex are owned by
        // the parent container so the input bar stays visible without an
        // outer scrollbar.
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        // The chat pane below scrolls its message list internally; the
        // panel itself never scrolls.
        overflow: 'hidden',
      }}
      data-testid="home-chat-panel"
      data-tour-id="home-chat-panel"
      data-display-mode={displayMode}
    >
      {mobileSessionsNavigation}
      <Box sx={{ display: 'flex', gap: isCompanion ? 0 : 2, flex: 1, minHeight: 0 }}>
        {/* Left rail */}
        {desktopSessionsRail}

        {/* Right pane — also the drag-and-drop zone. Drop handlers and the
            absolute-positioned <DropOverlay> are scoped to this column so
            dragging a file does NOT highlight the SessionsRail. */}
        <Box
          {...dragHandlers}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            border: isCompanion ? 0 : 1,
            borderColor: 'divider',
            borderRadius: isCompanion ? 0 : 1,
            overflow: 'hidden',
            // Scopes the absolute-positioned drop overlay to this column.
            position: 'relative',
            isolation: 'isolate',
          }}
          data-testid="home-chat-drop-zone"
        >
          {!isCompanion && <HomeChatWorkflowBanner
            title="Procurement Assistant"
            subtitle={isCompanion
              ? (documentCompanion ? 'Ask questions, find similar requirements, or request changes to this document.' : 'This is the conversation that started your current workflow.')
              : 'Ask questions about procurement, start a purchase, and find similar requirements.'}
            headingComponent={isCompanion ? 'h2' : 'h3'}
            action={completionSoundControl}
          />}


          {!isCompanion && mainSourceConversation && <Alert severity="info" action={<Button onClick={() => setMainControllerOpen(true)}>Continue workflow conversation</Button>}>Workflow setup and the conversation remain available in this session.</Alert>}
          {!isCompanion && !documentCompanion && workflowDocumentHistory?.sessionId === activeSessionId && workflowDocumentHistory.error && <Alert severity="warning">{workflowDocumentHistory.error}</Alert>}

          {/* Scrollable chat area */}
          <Box
            ref={threadScrollRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
            }}
            data-testid="home-chat-thread"
          >
            {showEmptyState && !(documentTurns.length || controllerTurns.length) ? (
              <Box
                sx={{
                  height: isCompanion ? 'auto' : '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: isCompanion ? 0 : 3,
                  color: 'text.secondary',
                }}
                data-testid="home-chat-empty"
              >
                {isCompanion ? (
                  null
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ display: { xs: 'none', md: 'block' } }}
                    >
                      Start a new chat or pick one from the left.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ display: { xs: 'block', md: 'none' } }}
                    >
                      Start a new chat or choose one from chat history.
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
              <>
                <Box sx={{ p: isCompanion ? 0 : 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                  ...(!setupAnchor && setupTranscript ? [setupTranscript] : []),
                  ...projectedItems.flatMap((entry) => {
                    const setup = entry === setupAnchor ? setupTranscript : null;
                    const renderEntry = () => {
                    if (entry.source === 'document') return <React.Fragment key={entry.turn.presentationKey || entry.turn.id}>
                      <DocumentAssistantTurn key={`${sourceScope}:${entry.turn.presentationKey || entry.turn.id}`} turn={entry.turn} generalAuthorized onSend={onDocumentSend} defaultExpanded={entry === latestCompletedEdit} />
                    </React.Fragment>;
                    const item = entry.item;
                    if (item.kind === 'legacy_message') {
                      return (
                        <ChatThread
                          key={`legacy-${item.index}-${item.timelineSequence ?? 'legacy'}`}
                          messages={[item.message]}
                          emptyPlaceholder={null}
                          compact
                        />
                      );
                    }
                    if (item.kind === 'card') {
                      // Once a valid selector click has been accepted locally,
                      // remove the chooser immediately. Its route-specific
                      // pending surface renders below, keyed by the same
                      // request id, until the server response replaces it.
                      if (pendingIntentChoiceSelection?.cardId === item.card.card_id) {
                        return null;
                      }
                      if (item.card.kind === 'training_action') {
                        // The server pairs this action with its answered turn.
                        // The turn renders the choice inside its response bubble.
                        return null;
                      }
                      return (
                        <SimilarRequirementsCards
                          key={`card-${item.card.card_id}`}
                          cards={[item.card]}
                          busyCardId={busySimilarRequirementsCardId || busyWorkflowRecommendationCardId}
                          focusNewPurchaseGuidanceCardId={newPurchaseGuidanceFocusId}
                          onPurchaseGuidanceHeadingFocused={() => setNewPurchaseGuidanceFocusId(null)}
                          onSelectType={handleSelectSimilarRequirementsType}
                          onCancel={handleCancelSimilarRequirements}
                          onOpen={handleOpenSimilarRequirementsResult}
                          onSelectIntentChoice={handleSelectAmbiguousIntentChoice}
                          onSelectWorkflowRecommendation={handleSelectWorkflowRecommendation}
                          onOpenCreatedWorkflow={handleOpenCreatedWorkflow}
                          onSelectActionChoice={handleSelectActionChoice}
                          onSelectMissionPartnerGuidance={handleSelectMissionPartnerGuidance}
                          onSelectPurchaseGuidance={handleSelectPurchaseGuidance}
                          workflowRecommendationBusy={busyWorkflowRecommendationCardId !== null}
                          actionChoicePendingLabel={busyActionChoiceLabel}
                        />
                      );
                    }
                    const trainingActionCard = chatCards.find((card) =>
                      card.kind === 'training_action'
                      && card.card_id === `training-${item.turn.turn_id}`
                    );
                    return (
                      <FarChatResponse
                        key={
                          item.turn.turn_id
                          || `turn-${item.index}-${item.timelineSequence ?? 'legacy'}`
                        }
                        turn={{
                          ...item.turn,
                          editable: item.turn.turn_id === editableLatestTurnId,
                        }}
                        workflowId={activeSessionId}
                        threadId={activeSessionId}
                        source="homechat"
                        trainingAction={trainingActionCard?.kind === 'training_action' ? {
                          card_id: trainingActionCard.card_id,
                          state: trainingActionCard.state,
                          error: trainingActionCard.error,
                          busy: busyTrainingActionId === trainingActionCard.card_id || isCompanion || !onOpenTraining,
                        } : undefined}
                        onSelectTrainingAction={handleSelectTrainingAction}
                        onEditTurn={handleBeginEdit}
                        editing={editingTurnId === item.turn.turn_id ? {
                          draft: editDraft,
                          onDraftChange: setEditDraft,
                          onSave: () => { void handleSaveAndResendEdit(); },
                          onCancel: handleCancelEdit,
                          saving: false,
                        } : null}
                        processingContent={
                          replacingTurnId === item.turn.turn_id && inFlight ? (
                            <Box
                              data-testid="home-chat-inline-replacement-processing"
                              data-turn-id={item.turn.turn_id}
                              aria-live="polite"
                              sx={{
                                alignSelf: 'flex-start',
                                maxWidth: '85%',
                                px: 1.5,
                                py: 1,
                                borderRadius: 2,
                                bgcolor: 'action.hover',
                                color: 'text.primary',
                                fontSize: '0.9rem',
                                boxShadow: 1,
                                minWidth: 220,
                              }}
                            >
                              <StepProgressChecklist
                                steps={progress.steps}
                                error={progress.error}
                                trailingAction={processingStopControl}
                                leadStep={progress.hasServerData
                                  ? null
                                  : buildLeadStep(true, false, 'Preparing your updated response')}
                              />
                            </Box>
                          ) : null
                        }
                      />
                    );
                    };
                    const rendered = renderEntry();
                    return [setup, rendered];
                  })]}
                  {/* A v2 in-flight turn normally uses the progress checklist
                      below; retain this loader for an in-flight request whose
                      progress metadata has not arrived yet. */}
                  {inFlight && !currentPurchaseClarification && !replacingTurnId && turns.length === 0 && !pendingRequestId && (
                    <ChatThread messages={[]} loading emptyPlaceholder={null} compact />
                  )}
                  {/* A reopened session can have server-owned activity before
                      its pending-request detail is available locally. Keep
                      that indicator in the transcript (never beneath the
                      composer) and keep its Stop control physically adjacent. */}
                  {activeSession?.activity && !inFlight && !replacingTurnId && (
                    <Box
                      data-testid="home-chat-transcript-processing-status"
                      role="status"
                      sx={{
                        alignSelf: 'flex-start',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        maxWidth: '85%',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: 'action.hover',
                        color: 'text.primary',
                        fontSize: '0.9rem',
                        boxShadow: 1,
                      }}
                    >
                      <CircularProgress size={18} aria-label={activeSession.activity.label} />
                      <Typography variant="body2">{activeSession.activity.label}</Typography>
                      {processingStopControl}
                    </Box>
                  )}
                </Box>
                {pendingGpcRouter && (
                  <Box
                    sx={{ m: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    {(pendingGpcRouter.kind === 'start_confirmation' || pendingGpcRouter.kind === 'ambiguous_choice') && (
                      <Box data-testid="home-chat-gpc-router-original-message" sx={{ alignSelf: 'flex-end', maxWidth: '85%', px: 1.5, py: 1, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText', whiteSpace: 'pre-wrap' }}>
                        {pendingGpcRouter.originalMessage}
                      </Box>
                    )}
                    <Box data-testid="home-chat-gpc-router-card" sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Typography sx={{ fontWeight: 600 }}>{pendingGpcRouter.prompt}</Typography>
                      {pendingGpcRouter.kind === 'ambiguous_choice' ? (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                          <Button variant="contained" onClick={() => void submitGpcConfirmation('confirmed')} disabled={inFlight}>
                            Start GPC purchase
                          </Button>
                          <Button variant="outlined" onClick={() => void submitGpcConfirmation('far_qa')} disabled={inFlight}>
                            Ask FAR/RFO question
                          </Button>
                          <Button variant="text" onClick={() => void submitGpcConfirmation('revise')} disabled={inFlight}>
                            Revise request
                          </Button>
                        </Box>
                      ) : pendingGpcRouter.kind === 'start_confirmation' ? (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                          <Button variant="contained" onClick={() => void submitGpcConfirmation('confirmed')} disabled={inFlight}>
                            Confirm and continue
                          </Button>
                          <Button variant="text" onClick={() => void submitGpcConfirmation('revise')} disabled={inFlight}>
                            Revise
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Select a saved GPC purchase in the next response to resume it.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {(!replacingTurnId && ((inFlight && pendingRequestId) ||
                  pendingClarification ||
                  pendingTopicClarification)) && (
                  <Box
                    data-testid="home-chat-v2-thread"
                    sx={{
                      // `pt: 2` mirrors the top padding ChatThread applies via
                      // its `p: 2` outer Box, so the first user bubble has
                      // consistent breathing room from the divider above
                      // regardless of whether it's rendered via the in-flight
                      // ChatThread path or the post-completion v2 path.
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    {inFlight && pendingRequestId && (
                      <>
                        {inFlightUserMsg && (
                          <Box
                            data-testid="farchat-user-bubble"
                            data-role="user"
                            data-in-flight="true"
                            sx={{
                              alignSelf: 'flex-end',
                              maxWidth: '85%',
                              px: 1.5,
                              py: 1,
                              borderRadius: 2,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              whiteSpace: 'pre-wrap',
                              fontSize: '0.9rem',
                              boxShadow: 0,
                              wordBreak: 'break-word',
                            }}
                          >
                            {inFlightUserMsg}
                          </Box>
                        )}
                        {/* In-flight "Clarified: …" callout — shown while
                            the reader/synth agents run, mirroring the
                            persisted-turn callout in FarChatResponse. Only
                            when the user actually clarified (final ≠
                            original). Cleared when the turn lands so the
                            persisted callout takes over (no duplicate). */}
                        {inFlightClarification && (
                          <Box
                            data-testid="farchat-clarified-callout"
                            data-in-flight="true"
                            className="farchat-clarified-callout"
                            sx={{
                              alignSelf: 'flex-end',
                              maxWidth: '85%',
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 2,
                              bgcolor: (theme) => alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                              color: 'text.primary',
                              border: '1px dashed',
                              borderColor: (theme) => alpha(theme.palette.info.main, 0.55),
                              fontSize: '0.8rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{ fontWeight: 700, mr: 0.5 }}
                            >
                              Clarified:
                            </Box>
                            {inFlightClarification}
                          </Box>
                        )}
                        {/* In-flight "Focused on: …" callout — shown while
                            the focused synth runs, mirroring the persisted-
                            turn callout in FarChatResponse. Only when the user
                            picked a non-empty focus (not Skip). */}
                        {inFlightTopicFocus && (
                          <Box
                            data-testid="farchat-focused-callout"
                            data-in-flight="true"
                            className="farchat-focused-callout"
                            sx={{
                              alignSelf: 'flex-end',
                              maxWidth: '85%',
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 2,
                              bgcolor: (theme) => alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                              color: 'text.primary',
                              border: '1px dashed',
                              borderColor: (theme) => alpha(theme.palette.success.main, 0.55),
                              fontSize: '0.8rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{ fontWeight: 700, mr: 0.5 }}
                            >
                              Focused on:
                            </Box>
                            {inFlightTopicFocus}
                          </Box>
                        )}
                        <Box
                          data-testid="home-chat-loading-bubble"
                          data-role="assistant"
                          aria-live="polite"
                          aria-label="Assistant is processing"
                          sx={{
                            alignSelf: 'flex-start',
                            maxWidth: '85%',
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: 'action.hover',
                            color: 'text.primary',
                            fontSize: '0.9rem',
                            boxShadow: 1,
                            minWidth: 220,
                          }}
                        >
                          <StepProgressChecklist
                            steps={progress.steps}
                            error={progress.error}
                            trailingAction={processingStopControl}
                            leadStep={progress.hasServerData
                              ? null
                              : buildLeadStep(
                                true,
                                false,
                                'Understanding your request'
                              )}
                          />
                        </Box>
                      </>
                    )}
                    {/* Live clarification card — transient, shown in place
                        of an answer while we wait for the user to
                        pick/refine. Hidden while a turn is in flight so it
                        never overlaps the loading checklist. The original
                        query renders as the user bubble above it. On
                        resolve we resubmit with `final_question`. */}
                    {!inFlight && pendingClarification && (
                      <>
                        <Box
                          data-testid="farchat-user-bubble"
                          data-role="user"
                          data-clarification-original="true"
                          sx={{
                            alignSelf: 'flex-end',
                            maxWidth: '85%',
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.9rem',
                            boxShadow: 0,
                            wordBreak: 'break-word',
                          }}
                        >
                          {pendingClarification.original_query}
                        </Box>
                        <ClarificationCard
                          enhancements={pendingClarification.enhancements}
                          originalQuery={pendingClarification.original_query}
                          regime={pendingClarification.regime}
                          onResolve={handleResolveClarification}
                          disabled={inFlight}
                        />
                      </>
                    )}
                    {/* Live topic-focus card — transient, shown in place of
                        an answer while we wait for the user to pick a focus /
                        skip. Hidden while a turn is in flight. The original
                        query renders as the user bubble above it. On resolve
                        we resubmit with `focus_token` + `topic_focus`. */}
                    {!inFlight && pendingTopicClarification && (
                      <>
                        <Box
                          data-testid="farchat-user-bubble"
                          data-role="user"
                          data-topic-focus-original="true"
                          sx={{
                            alignSelf: 'flex-end',
                            maxWidth: '85%',
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.9rem',
                            boxShadow: 0,
                            wordBreak: 'break-word',
                          }}
                        >
                          {pendingTopicClarification.original_query}
                        </Box>
                        {/* Card-phase "Clarified: …" callout — when this
                            topic-focus round followed a pre-reader
                            clarification, the readers ran on the clarified
                            question. Render it here so the callout stays
                            visible CONTINUOUSLY from the pre-reader
                            clarification through the topic-card phase and into
                            the focus resubmit in-flight phase (Bug 1). Reuses
                            the in-flight callout's styling + testid/className. */}
                        {pendingTopicClarification.clarification && (
                          <Box
                            data-testid="farchat-clarified-callout"
                            data-card-phase="true"
                            className="farchat-clarified-callout"
                            sx={{
                              alignSelf: 'flex-end',
                              maxWidth: '85%',
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 2,
                              bgcolor: (theme) => alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                              color: 'text.primary',
                              border: '1px dashed',
                              borderColor: (theme) => alpha(theme.palette.info.main, 0.55),
                              fontSize: '0.8rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{ fontWeight: 700, mr: 0.5 }}
                            >
                              Clarified:
                            </Box>
                            {pendingTopicClarification.clarification}
                          </Box>
                        )}
                        <TopicFocusCard
                          question={pendingTopicClarification.question}
                          topics={pendingTopicClarification.topics}
                          originalQuery={
                            pendingTopicClarification.original_query
                          }
                          onChoose={handleResolveTopicFocus}
                          disabled={inFlight}
                        />
                      </>
                    )}
                  </Box>
                )}
                {currentPurchaseClarification && inFlightUserMsg && (
                  <Box sx={{ p: 2 }}>
                    <ChatThread compact messages={[{ role: 'user', content: inFlightUserMsg }]} loading emptyPlaceholder={null} />
                  </Box>
                )}
                {currentPurchaseClarification && failedPurchaseFollowup && !inFlight && (
                  <Box role="alert" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                    <ChatThread compact messages={[{ role: 'user', content: failedPurchaseFollowup.message }]} emptyPlaceholder={null} />
                    <Typography variant="body2">That follow-up did not complete. You can retry it here.</Typography>
                    <Button variant="outlined" onClick={() => void handlePurchaseClarification(failedPurchaseFollowup.message, failedPurchaseFollowup.requestId)}>Retry follow-up</Button>
                  </Box>
                )}
              </>
            )}
            {isCompanion && <Box data-role="assistant" sx={{ flexShrink: 0 }}>
              {documentCompanion && controllerAvailable && <Alert severity="info" action={<Button onClick={onOpenController}>Continue new workflow conversation</Button>}>Your new workflow conversation is ready. You can return to this document chat at any time.</Alert>}
              {mainSourceConversation && <Alert severity="info" action={<Button onClick={() => setMainControllerOpen(true)}>Continue workflow conversation</Button>}>Workflow setup and the conversation remain available in this session.</Alert>}
              {workflowDocumentHistory?.sessionId === activeSessionId && workflowDocumentHistory.error && <Alert severity="warning">{workflowDocumentHistory.error}</Alert>}
              {documentCompanion && documentAssistant.historyWarning && <Alert severity="warning">{documentAssistant.historyWarning}</Alert>}
              {documentCompanion && documentAssistant.unknownOutcome && <Alert severity="warning" action={<Button onClick={documentAssistant.acknowledgeUnknown}>Acknowledge uncertainty</Button>}>The submitted edit may still complete. Review the current document and History before leaving or submitting again.</Alert>}
              <AttachmentChipRow attachments={attachments} onRemove={handleRemoveAttachment} disabled={inFlight} />
            </Box>}
          </Box>

          {!isCompanion && <AttachmentChipRow attachments={attachments} onRemove={handleRemoveAttachment} disabled={inFlight} />}

          {/* Input area — pinned at the bottom of the flex column. The
              `flexShrink: 0` guard prevents the textarea from being
              squeezed off-screen by a tall message list above. */}
          <Box ref={inputContainerRef} sx={{ flexShrink: 0 }}>
            {documentCompanion ? <DocumentAssistantComposer generalAuthorized onSend={onDocumentSend} disabled={inFlight} /> : currentPurchaseClarification ? <PurchaseFollowupInput
              onSend={handlePurchaseClarification}
              placeholder={composerPlaceholder}
              disabled={inFlight}
            /> : <MessageInput
              onSend={handleSend}
              onAttach={handleAttach}
              minimal={isCompanion}
              placeholder={isCompanion ? WORKFLOW_QUERY_PLACEHOLDER : composerPlaceholder}
              disabled={inFlight}
              pending={inFlight}
            />}
          </Box>

          {/* Drag-and-drop overlay scoped to the chat column (does NOT
              cover the SessionsRail). */}
          <DropOverlay visible={isDragging} />
        </Box>
      </Box>

      {/* Panel-local snackbar for transient feedback (renames, deletes,
          409 conflicts, generic errors). */}
      <Snackbar
        open={notice !== null}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? (
          <Alert
            severity={notice.severity}
            onClose={() => setNotice(null)}
            variant="filled"
          >
            {notice.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Paper>
  );
};

export default HomeChatPanelView;
